import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScoreEntry, Team } from '@/types/cricket';
import { getBallColor, getBallDisplay, groupBallsByOver, sortBallsInOver, BALL_COLORS, isPureExtraBall, isReviewEvent } from '@/utils/cricket/ballCalculations';
import { getInningsDisplay, getTeamName, formatOverNumber, getBallButtonClass, formatBallDisplay } from '@/utils/cricket/displayUtils';
import { isExtraBall, isWicketBall, isBoundaryBall, isOverthrowBall, isRegularRunBall, isDotBall } from '@/utils/cricket/types';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";

interface MatchOverviewPanelProps {
  scoreHistory: ScoreEntry[];
  teams: Team[];
  currentInnings: number;
  currentBattingTeam: string;
  onBallClick: (ball: ScoreEntry) => void;
  totalOvers: number;
  ballsPerOver: number;
}

export const MatchOverviewPanel: React.FC<MatchOverviewPanelProps> = ({
  scoreHistory,
  teams,
  currentInnings,
  currentBattingTeam,
  onBallClick,
  totalOvers,
  ballsPerOver
}) => {
  const [openInnings, setOpenInnings] = React.useState<number | null>(currentInnings);

  // Group balls by innings and overs
  const groupedBalls = groupBallsByOver(scoreHistory);

  // Sort balls within each over
  Object.keys(groupedBalls).forEach(key => {
    groupedBalls[key] = sortBallsInOver(groupedBalls[key]);
  });

  const colorLegend = [
    { color: BALL_COLORS.NOT_BOWLED, description: 'Not Bowled Yet' },
    { color: BALL_COLORS.MULTI_SCORE, description: 'Multi Score Types' },
    { color: BALL_COLORS.WICKET, description: 'Wicket' },
    { color: BALL_COLORS.NO_BALL_WIDE, description: 'No Ball / Wide' },
    { color: BALL_COLORS.BYE_LEG_BYE, description: 'Bye / Leg Bye' },
    { color: BALL_COLORS.OVERTHROW, description: 'Overthrow' },
    { color: BALL_COLORS.FOUR, description: 'Four (Boundary)' },
    { color: BALL_COLORS.SIX, description: 'Six (Boundary)' },
    { color: BALL_COLORS.REGULAR_RUN, description: 'Regular Run(s)' },
    { color: BALL_COLORS.DOT_BALL, description: 'Dot Ball' },
  ];

  const getTooltipContent = (ball: ScoreEntry | undefined) => {
    if (!ball) return "No ball bowled yet.";

    const contentLines: { label?: string; value: string; colorClass?: string; isType?: boolean }[] = [];

    // Determine if it's a multi-score ball (runs, boundary, extras, overthrows, etc.)
    const hasRuns = (ball.runs || 0) > 0;
    const hasBonus = (ball.bonus_boundary || 0) > 0;
    const hasExtras = (ball.extras || 0) > 0;
    const hasOverthrows = (ball.overthrows || 0) > 0;
    const scoreTypeCount = [hasRuns, hasBonus, hasExtras, hasOverthrows].filter(Boolean).length;
    if (scoreTypeCount >= 2) {
      if (hasRuns) {
        contentLines.push({ label: "Runs:", value: `${ball.runs} Run${ball.runs > 1 ? 's' : ''}`, colorClass: "text-blue-500" });
      }
      if (hasBonus) {
        if (ball.bonus_boundary === 4) {
          contentLines.push({ label: "Boundary:", value: "4 Runs", colorClass: "text-green-500" });
        } else if (ball.bonus_boundary === 6) {
          contentLines.push({ label: "Boundary:", value: "6 Runs", colorClass: "text-emerald-500" });
        }
      }
      if (hasExtras) {
        let extraDisplay = "";
        let extraColorClass = "text-yellow-500";
        if (ball.extraType === 'wide') extraDisplay = "Wide";
        else if (ball.extraType === 'noball') extraDisplay = "No Ball";
        else if (ball.extraType === 'bye') { extraDisplay = "Bye"; extraColorClass = "text-orange-500"; }
        else if (ball.extraType === 'legbye') { extraDisplay = "Leg Bye"; extraColorClass = "text-orange-500"; }
        const runLabel = ball.extras === 1 ? 'Run' : 'Runs';
        contentLines.push({ label: "Extra:", value: `${extraDisplay} (${ball.extras} ${runLabel})`, colorClass: extraColorClass });
      }
      if (hasOverthrows) {
        contentLines.push({ label: "Overthrow:", value: `${ball.overthrows} Runs`, colorClass: "text-purple-500" });
      }
    } else {
      if (isDotBall(ball)) {
        contentLines.push({ label: "Outcome:", value: "Dot Ball", colorClass: "text-gray-700" });
      }
      // Show both runs and boundary if both are present
      if (hasRuns && hasBonus) {
        contentLines.push({ label: "Runs:", value: `${ball.runs} Run${ball.runs > 1 ? 's' : ''}`, colorClass: "text-blue-500" });
        if (ball.bonus_boundary === 4) {
          contentLines.push({ label: "Boundary:", value: "4 Runs", colorClass: "text-green-500" });
        } else if (ball.bonus_boundary === 6) {
          contentLines.push({ label: "Boundary:", value: "6 Runs", colorClass: "text-emerald-500" });
        }
      } else {
        if (isRegularRunBall(ball) && ball.runs > 0) {
          contentLines.push({ label: "Runs:", value: `${ball.runs} Run${ball.runs > 1 ? 's' : ''}`, colorClass: "text-blue-500" });
        }
        if (isBoundaryBall(ball)) {
          if (ball.fours === 1 || ball.bonus_boundary === 4) {
            contentLines.push({ label: "Boundary:", value: "4 Runs", colorClass: "text-green-500" });
          }
          if (ball.sixes === 1 || ball.bonus_boundary === 6) {
            contentLines.push({ label: "Boundary:", value: "6 Runs", colorClass: "text-emerald-500" });
          }
        }
        // Only add Overthrow in single-score section if not already added
        if (isOverthrowBall(ball)) {
          contentLines.push({ label: "Overthrow:", value: `${ball.overthrows} Runs`, colorClass: "text-purple-500" });
        }
      }
      if (isExtraBall(ball)) {
        let extraDisplay = "";
        let extraColorClass = "text-yellow-500"; // Default for noball/wide
        if (ball.extraType === 'wide') extraDisplay = "Wide";
        else if (ball.extraType === 'noball') extraDisplay = "No Ball";
        else if (ball.extraType === 'bye') { extraDisplay = "Bye"; extraColorClass = "text-orange-500"; }
        else if (ball.extraType === 'legbye') { extraDisplay = "Leg Bye"; extraColorClass = "text-orange-500"; }
        const runLabel = ball.extras === 1 ? 'Run' : 'Runs';
        contentLines.push({ label: "Extra:", value: `${extraDisplay} (${ball.extras} ${runLabel})`, colorClass: extraColorClass });
      }
    }
    if (isWicketBall(ball)) {
      contentLines.push({ label: "Wicket:", value: "OUT!", colorClass: "text-red-500" });
      if (ball.wicketType) contentLines.push({ label: "Wicket Type:", value: `${ball.wicketType}` });
      if (ball.dismissedPlayerId) contentLines.push({ label: "Dismissed Player ID:", value: `${ball.dismissedPlayerId}` });
    }
    
    // Check for review events
    if (ball.description?.includes('Review') && ball.description?.includes('NOT OUT')) {
      contentLines.push({ label: "Review:", value: "NOT OUT", colorClass: "text-pink-500" });
      // Extract wicket type from description
      const wicketTypeMatch = ball.description.match(/Review - (\w+) -/);
      if (wicketTypeMatch) {
        contentLines.push({ label: "Appeal Type:", value: wicketTypeMatch[1], colorClass: "text-pink-500" });
      }
    }
    if (ball.isFreeHit) {
      contentLines.push({ label: "Special:", value: "Free Hit", colorClass: "text-orange-500" });
    }
    if (ball.commentary) {
      contentLines.push({ label: "Commentary:", value: `"${ball.commentary}"`, colorClass: "text-gray-600" });
    }
    if (ball.batsmanId) contentLines.push({ label: "Batsman ID:", value: `${ball.batsmanId}` });

    return contentLines.length > 0 ? (
      <div className="flex flex-col text-xs space-y-1">
        {contentLines.map((item, index) => (
          <div key={index} className="flex justify-between items-center w-full">
            {item.label && (
              item.isType ? (
                <span className="text-gray-600 mr-2 font-bold">{item.label}</span>
              ) : (
                <span className="text-gray-600 mr-2">{item.label}</span>
              )
            )}
            {item.isType ? (
              <span className={item.colorClass || "text-foreground"} style={{ fontWeight: 'bold' }}>{item.value}</span>
            ) : (
              <span className={item.colorClass || "text-foreground"}>{item.value}</span>
            )}
          </div>
        ))}
      </div>
    ) : "No details available";
  };

  return (
    <TooltipProvider>
      <Collapsible defaultOpen className="group">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Match Overview</CardTitle>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-9 p-0 border border-input">
                <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                <span className="sr-only">Toggle</span>
              </Button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-6">
              {teams.length >= 2 ? (
                [1, 2].map((innings) => {
                  let battingTeamIdForThisInnings: string | undefined;

                  if (innings === currentInnings) {
                    battingTeamIdForThisInnings = currentBattingTeam;
                  } else {
                    const otherTeam = teams.find(t => t.id !== currentBattingTeam);
                    battingTeamIdForThisInnings = otherTeam?.id;
                  }

                  const team = teams.find(t => t.id === battingTeamIdForThisInnings);
                  const isOpen = openInnings === innings;
                  
                  if (!team) {
                    return (
                      <div key={innings} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">
                            {getInningsDisplay(innings)} Innings - N/A (Team not found for ID: {battingTeamIdForThisInnings || 'Unknown'})
                          </h3>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <Collapsible
                      key={innings}
                      open={isOpen}
                      onOpenChange={() => setOpenInnings(isOpen ? null : innings)}
                      className="space-y-2"
                    >
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center w-full cursor-pointer gap-2">
                          <h3 className="font-semibold whitespace-nowrap">
                            {getInningsDisplay(innings)} Innings - {team.name}
                          </h3>
                          {innings === currentInnings && (
                            <Badge className="bg-green-500">Current</Badge>
                          )}
                          <div className="flex-grow border-t border-dashed mx-2" />
                          <ChevronDown
                            className={`h-4 w-4 transition-transform duration-200 shrink-0 ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="grid grid-cols-1 gap-4 pt-4">
                        {Array.from({ length: totalOvers }, (_, i) => {
                          const overNumber = i;
                          const key = `${battingTeamIdForThisInnings}-${overNumber}`;
                          const balls = groupedBalls[key] || [];

                          return (
                            <div key={overNumber} className="space-y-2">
                              <div className="text-sm font-medium text-gray-600">
                                Over {formatOverNumber(overNumber)}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {/* Regular balls */}
                                <div className="flex gap-1">
                                  {Array.from({ length: ballsPerOver }, (_, i) => {
                                    const ball = balls.find(b => b.ballNumber === i);
                                    const ballId = `${battingTeamIdForThisInnings}-${overNumber}-${i}`;
                                    const isExtraBallType = ball ? isExtraBall(ball) : false;
                                    const isFreeHit = ball?.isFreeHit;

                                    // Determine if previous ball is a pure extra
                                    const prevBall = balls.find(b => b.ballNumber === i - 1);
                                    const prevIsPureExtra = prevBall ? isPureExtraBall(prevBall) : false;
                                    // If this ball is not yet bowled and previous was a pure extra, mark as inactive
                                    const isInactive = !ball && prevIsPureExtra;

                                    // Use correct color for pure extra balls
                                    let ballColor = getBallColor(ball);
                                    if (!ball && prevIsPureExtra) {
                                      // If this is the next ball after a pure extra, show as inactive (faded)
                                      ballColor = BALL_COLORS.NOT_BOWLED;
                                    }

                                    const displayContent = isFreeHit
                                      ? 'FH'
                                      : (isExtraBallType ? '+' : formatBallDisplay(overNumber, i, ballsPerOver));

                                    return (
                                      <React.Fragment key={ballId}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className={getBallButtonClass(isExtraBallType, isFreeHit) + (isInactive ? ' opacity-40 pointer-events-none' : '')}
                                              style={{ backgroundColor: ballColor }}
                                              onClick={() => ball && onBallClick(ball)}
                                              disabled={!ball || isInactive}
                                            >
                                              <div className="flex flex-col items-center justify-center text-xs">
                                                <span>{displayContent}</span>
                                                {ball && <span className="text-[10px] font-medium">{getBallDisplay(ball)}</span>}
                                              </div>
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent
                                            side="top"
                                            align="center"
                                          >
                                            {getTooltipContent(ball)}
                                          </TooltipContent>
                                        </Tooltip>

                                        {isExtraBallType && ball && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className={getBallButtonClass(true, isFreeHit)}
                                                style={{ backgroundColor: isPureExtraBall(ball) ? BALL_COLORS.NOT_BOWLED : ballColor }}
                                                onClick={() => onBallClick(ball)}
                                                disabled={isPureExtraBall(ball)}
                                              >
                                                <div className="flex flex-col items-center justify-center text-xs">
                                                  <span>+</span>
                                                  <span className="text-[10px]">{ball.extraType}</span>
                                                </div>
                                              </Button>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              align="center"
                                            >
                                              {getTooltipContent(ball)}
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>

                                {/* Extra balls outside of regular overs */}
                                {balls.filter(b => b.ballNumber >= ballsPerOver).map((ball, index) => {
                                  const ballId = `${battingTeamIdForThisInnings}-${overNumber}-extra-${index}`;
                                  const isExtraBallType = isExtraBall(ball);
                                  const isFreeHit = ball.isFreeHit;
                                  const ballColor = getBallColor(ball);

                                  const displayContent = isFreeHit
                                    ? 'FH'
                                    : (isExtraBallType ? '+' : formatBallDisplay(overNumber, ball.ballNumber, ballsPerOver));

                                  return (
                                    <React.Fragment key={ballId}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className={getBallButtonClass(isExtraBallType, isFreeHit)}
                                            style={{ backgroundColor: ballColor }}
                                            onClick={() => onBallClick(ball)}
                                          >
                                            <div className="flex flex-col items-center justify-center text-xs">
                                              <span>{displayContent}</span>
                                              <span className="text-[10px] font-medium">{getBallDisplay(ball)}</span>
                                            </div>
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          align="center"
                                        >
                                          {getTooltipContent(ball)}
                                        </TooltipContent>
                                      </Tooltip>

                                      {isExtraBallType && (
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className={getBallButtonClass(true, isFreeHit)}
                                              style={{ backgroundColor: isPureExtraBall(ball) ? BALL_COLORS.NOT_BOWLED : ballColor }}
                                              onClick={() => onBallClick(ball)}
                                              disabled={isPureExtraBall(ball)}
                                            >
                                              <div className="flex flex-col items-center justify-center text-xs">
                                                <span>+</span>
                                                <span className="text-[10px]">{ball.extraType}</span>
                                              </div>
                                            </Button>
                                          </TooltipTrigger>
                                          <TooltipContent
                                            side="top"
                                            align="center"
                                          >
                                            {getTooltipContent(ball)}
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })
              ) : (
                <div className="text-red-500">Not enough teams to display match overview.</div>
              )}

              <div className="mt-4 pt-4 border-t">
                <h4 className="font-semibold mb-2 text-sm">Ball Button Color Legend</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                  {colorLegend.map(({ color, description }) => (
                    <div key={description} className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: color }} />
                      <span className="text-xs">{description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </TooltipProvider>
  );
};